import type { MouseEvent } from 'react'
import { Fragment, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import {
  Box,
  Separator,
  SideMenuLayoutMenuItem,
  useSecondaryMenu,
} from '@enechain/ecloud-designsystem'
import { FaGearRegular } from '@enechain/ecloud-icons'

import { getGroupIcon } from '~/components/layouts/MainLayout/DottedGroupIcon'
import { SubMenuItems } from '~/components/layouts/MainLayout/SubMenuItems'
import { AnnouncementsMenuIcon } from '~/components/layouts/MainLayout/TopMenu/AnnouncementsMenuIcon'
import {
  type MenuGroup,
  getGroupLandingPath,
  hasSubMenu,
  isMenuPathActive,
  isPathInGroup,
  toSubMenuItems,
} from '~/components/layouts/MainLayout/menuGroups'
import {
  clearSecondaryMenuClosedByUser,
  hasMenuGroupForPathname,
  isSecondaryMenuClosedByUser,
  resolveAutoOpenPrefix,
} from '~/components/layouts/MainLayout/secondaryMenuAutoOpen'
import {
  SIDE_MENU_DOT_ATTR,
  sideMenuRowPulseStyle,
} from '~/components/layouts/MainLayout/sideMenuDotPulse'
import { useVisibleMenuGroups } from '~/components/layouts/MainLayout/useVisibleMenuGroups'
import { openOrNavigate } from '~/libs/openOrNavigate'
import { routePaths } from '~/routes/routePaths'

export const TopMenu = (): React.ReactElement => {
  const { t } = useTranslation()
  const visibleMenuGroups = useVisibleMenuGroups()
  const location = useLocation()
  const navigate = useNavigate()
  const { setSecondaryMenu, clearSecondaryMenu, currentSecondaryMenu } =
    useSecondaryMenu()

  // 現在 URL が属する group の副パネルを開く。 SPA 内の遷移でも追従させるため
  // pathname を依存に持つ (= mount 時のみだと、 画面内リンクで別 group へ移った時に
  // 前の group の副パネルが残る)。 design `include-nav.js` も毎ページで現在 URL から
  // group を判定して auto-open する。
  //
  // × で閉じた後は auto-open だけを抑止する (nav を自分でクリックした時は開く)。
  // 抑止しないと「閉じたのに遷移のたびに勝手に開き直す」 挙動になる。
  useEffect(() => {
    // group を持たない画面 (ホーム / 提出管理 / 個人設定) では副パネルを閉じる。
    // 出すべき副パネルが無いので、 残すと「開いている画面と無関係な group の
    // メニューが出ている」 状態になる (デザイナー指摘 2026-08-12「個人設定を
    // 開いているのに sub side menu がシステム管理が開いたまま」)。
    // design は multi-page で遷移ごとに副パネルごと作り直されるため顕在化しない。
    //
    // **viewport 幅で条件分けしない** (レビュー指摘 2026-08-12)。
    // `resolveAutoOpenPrefix` の 768px ガードは「狭い画面で副パネルが本文を**覆う**」
    // のを防ぐためのもので、 抑止したいのは開く方向だけ。 閉じる方向は本文を覆わない
    // ので同じ理由が効かない。 むしろ狭い画面ほど、 現在の画面と無関係な group の
    // パネルが本文に覆いかぶさったまま残る害が大きい。
    // `closedByUser` も同様に見ない (既に閉じているので clear は no-op)。
    if (!hasMenuGroupForPathname(location.pathname)) {
      clearSecondaryMenu()
      return
    }
    const prefix = resolveAutoOpenPrefix(location.pathname, {
      closedByUser: isSecondaryMenuClosedByUser(),
      viewportWidth: window.innerWidth,
    })
    if (prefix !== undefined) {
      setSecondaryMenu(prefix)
    }
    // setSecondaryMenu / clearSecondaryMenu は DS context 由来で毎 render 新しい関数に
    // なるため依存に入れない (入れると pathname が変わっていなくても副パネルを
    // 開き直してしまう)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // group を自分でクリックして副パネルを開く = 見る意思表示なので、 過去に × で
  // 閉じた記憶を捨てる (残すと、 遷移先で auto-open が抑止されたままになる)。
  // **実際に現在タブで開く時だけ**呼ぶ (別タブ遷移では現在タブの記憶を触らない)。
  const openSecondaryMenu = (prefix: string): void => {
    clearSecondaryMenuClosedByUser()
    setSecondaryMenu(prefix)
  }

  const handleClick = (
    group: MenuGroup,
    title: string,
    e?: MouseEvent,
  ): void => {
    // グループ見出しクリック時、 下層メニューが存在する場合は副パネルを開いた上で
    // 表示中 items の先頭 (= getGroupLandingPath) へ自動遷移する
    // (`docs/specs/common/shared-components.md` AC-SC-029、 他プロダクト共通の挙動)。
    // cmd/ctrl+クリック時は副パネルを開かず別タブで開く。
    const landingPath = getGroupLandingPath(group)
    if (landingPath !== undefined) {
      if (openOrNavigate(navigate, landingPath, e)) {
        // 別タブで開いた = 現在タブの副パネルは開かない。 「× で閉じた」 記憶は
        // 現在タブ (sessionStorage) のものなので、 ここで解除してはいけない
        // (解除すると、 閉じたはずの副パネルが次の遷移で開き直す)。
        return
      }
      // 単一リンクの group (= システム管理、 #9228) には副パネルが無いので開かない。
      // 開こうとすると存在しない prefix が選択状態になり、 前の group の副パネルが残る。
      if (hasSubMenu(group)) {
        openSecondaryMenu(group.prefix)
      }
      return
    }
    if (currentSecondaryMenu?.title === title) {
      clearSecondaryMenu()
    } else {
      openSecondaryMenu(group.prefix)
    }
  }

  return (
    // design #9306 ③: ドットを子に持つ行 (DS の menu item の button) の地色を明滅させる。
    // design の `:has()` と同じ手で、DS の内部 class には依存しない (button 要素 + ドットの data 属性)。
    //
    // [DS差異] DS の menu item に行の明滅が無いため、部品の外側 (この wrapper) から当てる。
    // `animation` は hover / active の背景より優先されるので、選択中の行も警告色に置き換わる
    // (design どおり)。理由: design #9306 ③ の行の明滅が DS の menu item に無い。
    <Box
      display="contents"
      css={{
        [`& button:has([${SIDE_MENU_DOT_ATTR}="warning"])`]:
          sideMenuRowPulseStyle('warning'),
        [`& button:has([${SIDE_MENU_DOT_ATTR}="negative"])`]:
          sideMenuRowPulseStyle('negative'),
      }}
    >
      {visibleMenuGroups.map((group) => {
        const title = t(group.labelKey)
        // DS は `secondaryMenu` の有無で「右端 chevron の表示」 (= `MenuItemIcon`
        // の `hasSecondaryMenu`) と「hover popup の描画」 の両方を決めるため、
        // 下層画面を持つ group には必ず渡す。 副 panel 用の record (MainLayout) は
        // panel 専用の close button を含む `SecondaryMenuContent` なので、 popup に
        // は `SubMenuItems` だけを渡す (= 同じ object を使い回さない)。
        return (
          <Fragment key={group.prefix}>
            {/* design のトップレベル セクション区切り線 (`design/css/layout.css` の
                `.nav-divider` = 1px / Border.lowEmphasis / margin 4px 8px)。
                DS `Separator` の既定色が Border.lowEmphasis なので色指定は不要。 */}
            {group.sectionDividerAbove === true && (
              <Separator marginX={2} marginY={1} />
            )}
            <SideMenuLayoutMenuItem
              IconLeft={getGroupIcon(group)}
              isActive={isPathInGroup(group, location.pathname)}
              label={title}
              onClick={(e?: MouseEvent) => handleClick(group, title, e)}
              // 単一リンクの group (= システム管理、 #9228) には渡さない。
              // 下層画面が無いので chevron も flyout も出さない (個人設定と同じ扱い)。
              secondaryMenu={
                hasSubMenu(group)
                  ? {
                      title,
                      // `data-ebalance-flyout` は hover popup の影を当てるための目印
                      // (`constants/system.ts` の globalCss 参照)。`display: contents` なので
                      // レイアウトには影響しない。副パネル側 (`SecondaryMenuContent`) には
                      // 付けない — 影を足すのは popup だけ。
                      menu: (
                        <Box display="contents" data-ebalance-flyout>
                          <SubMenuItems items={toSubMenuItems(group, t)} />
                        </Box>
                      ),
                    }
                  : undefined
              }
            />
          </Fragment>
        )
      })}
      {/* 個人設定: design HTML 踏襲で システム管理直後の top-level link
          (group flyout 無し、 navigate のみ)。 design/js/include-nav.js line 229
          の `{ type: 'link', ic: 'gear', href: '/settings.html', label: '個人設定' }`
          相当。 下層画面が無いので secondaryMenu は渡さない (= chevron も出ない)。 */}
      <SideMenuLayoutMenuItem
        IconLeft={FaGearRegular}
        isActive={location.pathname === routePaths.settings.path}
        label={t('common:nav.settings')}
        onClick={(e?: MouseEvent) =>
          openOrNavigate(navigate, routePaths.settings.path, e)
        }
      />
      {/* お知らせ: design/js/include-nav.js の 個人設定 → divider → お知らせ の並び。
          プラン・権限・マスタ登録に依らず常時出す (design の _showUpdatesPage は
          プロトタイプの proto-mode 表示切替 (既定は表示) で、本番に対応する条件は無い)。
          icon は design の megaphone に当たるものが ecloud-icons に無いため、
          告知の意味で最も近い bell を使う。 */}
      <Separator marginX={2} marginY={1} />
      <SideMenuLayoutMenuItem
        IconLeft={AnnouncementsMenuIcon}
        isActive={isMenuPathActive(
          location.pathname,
          routePaths.announcements.path,
        )}
        label={t('common:nav.announcements')}
        onClick={(e?: MouseEvent) =>
          openOrNavigate(navigate, routePaths.announcements.path, e)
        }
      />
    </Box>
  )
}
